import { BranchCode, RealEstateDto } from "api/risk"
import { useEligibilityCheck } from "features/subscription-process/home-insurance/hooks/useEligibilityCheck"
import { useRgiRiskData, useSubscriptionProcessState } from "features/subscription-process/contexts/selectors/selectors"
import SimpleBooleanQuestion from "features/subscription-process/profiling-process/components/generic/SimpleBooleanQuestion"
import SimpleInputQuestion from "features/subscription-process/profiling-process/components/generic/SimpleInputQuestion"
import { PossibleAnswer } from "features/subscription-process/profiling-process/types/interfaces"
import { AlertCircle, ShieldCheck } from "lucide-react"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { risksAPI } from "services/swagger-api/riskApi"
import { toDayInput } from 'core/utils/dates'

const insuranceEffectiveDateQuestion = {
  key: "contractStartDate",
  label: "Quelle est la date d'effet du contrat d'assurance GLI ?",
  isMultipleChoice: false,
  possibleAnswers: []
}

const terminationClauseQuestion = {
  key: "terminationClause",
  label: "Le contrat de bail contient-il une clause résolutoire ?",
  isMultipleChoice: false,
  possibleAnswers: [true, false]
}

interface RgiRealEstateEligibilityProps {
  selectedRealEstate: RealEstateDto
  onEligibilityChange: (isEligible: boolean) => void
  setPostSubmit: React.Dispatch<React.SetStateAction<(() => Promise<void>) | undefined>>
}

export const RgiRealEstateEligibility: FC<RgiRealEstateEligibilityProps> = ({
  onEligibilityChange,
  setPostSubmit,
  selectedRealEstate
}) => {
  const [hasClause, setHasClause] = useState<boolean>(false)
  const [contractStartDate, setContractStartDate] = useState<string>()
  const { riskId } = useSubscriptionProcessState()
  const [dateError, setDateError] = useState<string>()

  // Verdict loyer + clause résolutoire calculé au back (source unique). La date reste validée ici.
  const { verdict } = useEligibilityCheck(BranchCode.RGI, selectedRealEstate?.id, {
    terminationClauseGlobal: hasClause
  })

  const rgiRiskData = useRgiRiskData()
  const subscriptionStartDate = rgiRiskData?.contractStartDate ? new Date(rgiRiskData.contractStartDate) : undefined

  useEffect(() => {
    if (subscriptionStartDate) {
      setContractStartDate(toDayInput(subscriptionStartDate))
      setHasClause(true)
    }

  }, [subscriptionStartDate])

  const today = useMemo(() => toDayInput(new Date()), [])

  const maxDate = useMemo(() => {
    const date = new Date()
    date.setMonth(date.getMonth() + 6)
    return toDayInput(date)
  }, [])

  const onAnswer = useCallback((key: string, answer: PossibleAnswer[]) => {
    if (key === 'terminationClause') {
      setHasClause(answer[0] as boolean)
    }
    if (key === 'contractStartDate') {
      const dateString = answer[0] as string
      setContractStartDate(dateString)

      if (dateString && dateString.length === 10) {
        const selectedDate = new Date(dateString)
        const todayDate = new Date()
        todayDate.setHours(0, 0, 0, 0)

        const maxDateObj = new Date()
        maxDateObj.setMonth(maxDateObj.getMonth() + 6)
        maxDateObj.setHours(0, 0, 0, 0)

        if (selectedDate < todayDate) {
          setDateError("La date ne peut pas être antérieure à aujourd'hui")
        } else if (selectedDate > maxDateObj) {
          setDateError("La date ne peut pas être supérieure à 6 mois à partir d'aujourd'hui")
        } else {
          setDateError(undefined)
        }
      } else {
        setDateError(undefined)
      }
    }
  }, [])

  const postSubmit = useCallback(async () => {
    if (riskId && contractStartDate) {
      try {
        await risksAPI.pathRGIRisk({
          riskId,
          patchRgiRiskCommandDto: {
            contractStartDate: new Date(contractStartDate)
          }
        })
      } catch (error) {
        console.error('Erreur lors de la mise à jour du risque:', error)
        throw error
      }
    }
  }, [riskId, contractStartDate])

  useEffect(() => {
    setPostSubmit(() => postSubmit)
  }, [postSubmit, setPostSubmit])

  const isDateValid = useMemo(() => {
    if (!contractStartDate || contractStartDate.length !== 10) {
      return false
    }
    return !dateError
  }, [contractStartDate, dateError])

  const allErrors = useMemo(() => {
    const errors: string[] = [...(verdict?.reasons ?? [])]
    if (dateError) errors.push(dateError)
    return errors
  }, [verdict, dateError])

  const isEligible = (verdict?.isEligible ?? false) && isDateValid

  useEffect(() => {
    onEligibilityChange(isEligible)
  }, [isEligible, onEligibilityChange])

  return (
    <div className="rounded-xl border border-primary/15 bg-primary/[0.04] p-4 space-y-3">
      {/* En-tête + statut d'éligibilité */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <h3 className="text-base font-semibold text-slate-900">Bail &amp; assurance</h3>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
            isEligible
              ? 'bg-green-50 text-green-700 border-green-200'
              : 'bg-red-50 text-red-700 border-red-200'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${isEligible ? 'bg-green-500' : 'bg-red-500'}`} />
          {isEligible ? 'Éligible' : 'Non éligible'}
        </span>
      </div>

      {/* Questions */}
      <div className="rounded-lg bg-white border border-gray-100 divide-y divide-gray-100">
        <div className="px-3 py-3">
          <SimpleBooleanQuestion
            question={terminationClauseQuestion}
            onAnswer={onAnswer}
            currentAnswers={[hasClause]}
          />
        </div>
        <div className="px-3 py-3">
          <SimpleInputQuestion
            question={insuranceEffectiveDateQuestion}
            onAnswer={onAnswer}
            currentAnswers={contractStartDate ? [contractStartDate] : []}
            min={today}
            max={maxDate}
            type='date'
          />
        </div>
      </div>

      {/* Erreurs d'éligibilité */}
      {allErrors.length > 0 && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-700">Critères non respectés</p>
              <ul className="mt-1 space-y-0.5">
                {allErrors.map((error, index) => (
                  <li key={index} className="text-sm text-red-600">• {error}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
